import { createSlice } from "@reduxjs/toolkit";
import * as EditorActions from "./actions";
import {append, lensPath, lensProp, map, pipe, prop, set} from "ramda";
import {SETTING_DEFAULT_COLORS} from "../../constants/editor";
import * as AppActions from "../app/actions";

export const initialState = {
  points: [],
  rectangles: [],
  currentPointerMode: null,
  currentDrawingRectangle: null,
  activeObjectId: null,
  activeDetectedFieldId: null,
  settingColors: {
    drawingObject: localStorage.getItem('drawingObject') || SETTING_DEFAULT_COLORS.DRAWING_OBJECT,
    currentObject: localStorage.getItem('currentObject') || SETTING_DEFAULT_COLORS.CURRENT_OBJECT,
    detectedField: localStorage.getItem('detectedField') || SETTING_DEFAULT_COLORS.DETECTED_FIELD
  },
  settingObjectDefaultSizes: {
    width: Number(localStorage.getItem('objectDefaultWidth')) || null,
    height: Number(localStorage.getItem('objectDefaultHeight')) || null,
  }
};

const templateSlice = createSlice({
  name: "PDFViewer",
  initialState,
  reducers: {},
  extraReducers: {
    [AppActions.resetAppState.type]: (state, action) => {
      return initialState;
    },
    [EditorActions.addPoint.type]: (state, action) => {
      return set(
        lensProp("points"),
        append(action.payload.point, state.points)
      )(state);
    },
    [EditorActions.addRectangle.type]: (state, action) => {
      return set(
        lensProp("rectangles"),
        append(action.payload.rectangle, state.rectangles)
      )(state);
    },
    [EditorActions.setCurrentPointerMode.type]: (state, action) => {
      return set(lensProp("currentPointerMode"), action.payload.mode)(state);
    },
    [EditorActions.setActiveObjectId.type]: (state, action) => {
      return pipe(
        set(lensProp("activeObjectId"), action.payload.id),
        set(lensProp("activeDetectedFieldId"), null)
      )(state);
    },
    [EditorActions.setActiveDetectedField.type]: (state, action) => {
      return pipe(
        set(lensProp("activeDetectedFieldId"), action.payload.activeDetectedFieldId),
        set(lensProp("activeObjectId"), null)
      )(state);
    },
    [EditorActions.changeRectangleFieldCoordinates.type]: (state, action) => {
      const newRectangles = pipe(
        prop("rectangles"),
        map((item) => {
          if (item.id === action.payload.id) {
            return {
              ...item,
              fieldProperties: {
                ...item.fieldProperties,
                rectangle: [
                  action.payload.x,
                  action.payload.y,
                  item.fieldProperties.rectangle[2],
                  item.fieldProperties.rectangle[3],
                ],
              },
            };
          }
          return item;
        })
      )(state);

      return set(lensProp("rectangles"), newRectangles)(state);
    },

    [EditorActions.changeRectangleFieldSizes.type]: (state, action) => {
      const newRectangles = pipe(
        prop("rectangles"),
        map((item) => {
          if (item.id === action.payload.id) {
            return {
              ...item,
              fieldProperties: {
                ...item.fieldProperties,
                rectangle: [
                  item.fieldProperties.rectangle[0],
                  item.fieldProperties.rectangle[1],
                  action.payload.w,
                  action.payload.h,
                ],
              },
            };
          }
          return item;
        })
      )(state);

      return set(lensProp("rectangles"), newRectangles)(state);
    },

    [EditorActions.setCurrentDrawingRectangle.type]: (state, action) => {
      return set(
        lensProp("currentDrawingRectangle"),
        action.payload.data
      )(state);
    },

    [EditorActions.setDrawingObjectColor.type]: (state, action) => {
      localStorage.setItem('drawingObject', action.payload.color);
      return set(
        lensPath(["settingColors", "drawingObject"]),
        action.payload.color
      )(state);
    },

    [EditorActions.setCurrentObjectColor.type]: (state, action) => {
      localStorage.setItem('currentObject', action.payload.color);
      return set(
        lensPath(["settingColors", "currentObject"]),
        action.payload.color
      )(state);
    },

    [EditorActions.setDetectedFieldColor.type]: (state, action) => {
      localStorage.setItem('detectedField', action.payload.color);
      return set(
        lensPath(["settingColors", "detectedField"]),
        action.payload.color
      )(state);
    },

    [EditorActions.setObjectDefaultSizes.type]: (state, action) => {
      localStorage.setItem('objectDefaultWidth', action.payload.data.width);
      localStorage.setItem('objectDefaultHeight', action.payload.data.height);
      return set(
        lensPath(["settingObjectDefaultSizes"]),
        action.payload.data
      )(state);
    },
  },
});

export const { actions, reducer, name: sliceKey } = templateSlice;
